import React, {useEffect, useState} from 'react';
import {
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Stack,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {getDisclosuresByParentId} from '../../../../firebase/firestore/reports';
import {formatValue} from '../../utils/formatters';
import {getReportStatusChipColor} from '../../utils/reportStatusColor';

/**
 * Modal to display list of disclosures for a parent report
 * @param {boolean} open - Modal open state
 * @param {function} onClose - Close handler
 * @param {string} parentReportId - Parent report ID to fetch disclosures for
 * @param {string} parentTitle - Title of parent report for display
 * @param {function} onDisclosureClick - Handler for clicking on a disclosure
 */
const DisclosuresListModal = ({open, onClose, parentReportId, parentTitle, onDisclosureClick}) => {
    const [disclosures, setDisclosures] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open || !parentReportId) {
            return;
        }

        const fetchDisclosures = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await getDisclosuresByParentId(parentReportId);
                setDisclosures(data);
            } catch (err) {
                console.error('Failed to fetch disclosures:', err);
                setError(err.message || 'Failed to load disclosures');
            } finally {
                setLoading(false);
            }
        };

        fetchDisclosures();
    }, [open, parentReportId]);

    const handleDisclosureClick = (disclosureId) => {
        if (onDisclosureClick) {
            onDisclosureClick(disclosureId);
        }
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Disclosures</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon/>
                    </IconButton>
                </Stack>
                {parentTitle && (
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                        Parent: {parentTitle}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Stack alignItems="center" py={3}>
                        <CircularProgress size={40}/>
                    </Stack>
                ) : error ? (
                    <Stack alignItems="center" py={3}>
                        <Typography color="error" variant="body2">
                            {error}
                        </Typography>
                    </Stack>
                ) : disclosures.length === 0 ? (
                    <Stack alignItems="center" py={3}>
                        <Typography color="text.secondary" variant="body2">
                            No disclosures found
                        </Typography>
                    </Stack>
                ) : (
                    <List disablePadding>
                        {disclosures.map((disclosure) => (
                            <ListItemButton
                                key={disclosure.id}
                                onClick={() => handleDisclosureClick(disclosure.id)}
                                sx={{
                                    borderRadius: 1,
                                    mb: 0.5,
                                    '&:hover': {
                                        backgroundColor: 'action.hover',
                                    },
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography variant="body1">
                                                {disclosure.title || 'Untitled'}
                                            </Typography>
                                            {disclosure.status && (
                                                <Chip
                                                    label={disclosure.status}
                                                    size="small"
                                                    color={getReportStatusChipColor(disclosure.status)}
                                                    sx={{height: 20}}
                                                />
                                            )}
                                        </Stack>
                                    }
                                    secondary={
                                        <Stack spacing={0.5} mt={0.5}>
                                            {disclosure.userDisplayName && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Author: {disclosure.userDisplayName}
                                                </Typography>
                                            )}
                                            {disclosure.updatedAt && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Updated: {formatValue(disclosure.updatedAt)}
                                                </Typography>
                                            )}
                                            {disclosure.meta?.disclosureType?.label && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Type: {disclosure.meta.disclosureType.label}
                                                </Typography>
                                            )}
                                        </Stack>
                                    }
                                />
                            </ListItemButton>
                        ))}
                    </List>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default DisclosuresListModal;
